import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import { parseScreenshot, createRun } from "../../lib/api";
import { formatTime } from "../../lib/format";

interface ParsedRun {
  date: string;
  timeSeconds: number;
  speed: number;
  points: number;
  selected: boolean;
}

export default function ImportScreenshotScreen() {
  const router = useRouter();
  const { dogId } = useLocalSearchParams<{ dogId: string }>();
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState({ current: 0, total: 0 });
  const [importing, setImporting] = useState(false);
  const [runs, setRuns] = useState<ParsedRun[]>([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function pickImages() {
    setError("");
    setSuccess("");

    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.multiple = true;
      input.onchange = async (e: any) => {
        const files = Array.from(e.target.files || []) as File[];
        if (files.length === 0) return;
        const base64Images: string[] = [];
        for (const file of files) {
          const b64 = await fileToBase64(file);
          base64Images.push(b64);
        }
        await parseAllImages(base64Images);
      };
      input.click();
    } else {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        quality: 0.8,
        base64: true,
        allowsMultipleSelection: true,
      });
      if (!result.canceled && result.assets.length > 0) {
        const base64Images = result.assets
          .filter((a) => a.base64)
          .map((a) => `data:image/jpeg;base64,${a.base64}`);
        if (base64Images.length > 0) {
          await parseAllImages(base64Images);
        }
      }
    }
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function parseAllImages(images: string[]) {
    setParsing(true);
    setError("");
    setParseProgress({ current: 0, total: images.length });

    const newRuns: ParsedRun[] = [];
    let errors = 0;

    for (let i = 0; i < images.length; i++) {
      setParseProgress({ current: i + 1, total: images.length });
      try {
        const result = await parseScreenshot(images[i]);
        if (result.runs && result.runs.length > 0) {
          newRuns.push(...result.runs.map((r) => ({ ...r, selected: true })));
        }
      } catch {
        errors++;
      }
    }

    // Merge with existing runs and deduplicate
    const combined = [...runs, ...newRuns];
    if (combined.length > 0) {
      const seen = new Set<string>();
      const deduped = combined.filter((r) => {
        const key = `${r.date}-${r.timeSeconds}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setRuns(deduped);
    } else if (newRuns.length === 0) {
      setError("No runs found in the screenshot" + (images.length > 1 ? "s" : "") + ". Make sure the run data table is visible.");
    }

    if (errors > 0 && newRuns.length > 0) {
      setError(`${errors} screenshot${errors > 1 ? "s" : ""} couldn't be read, but we found runs in the others.`);
    }

    setParsing(false);
  }

  function toggleRun(index: number) {
    setRuns((prev) => prev.map((r, i) => i === index ? { ...r, selected: !r.selected } : r));
  }

  async function handleImport() {
    const selected = runs.filter((r) => r.selected);
    if (selected.length === 0) {
      setError("Select at least one run to import");
      return;
    }

    setImporting(true);
    setError("");
    try {
      let imported = 0;
      for (const run of selected) {
        await createRun(dogId!, {
          date: run.date,
          timeSeconds: run.timeSeconds,
        });
        imported++;
      }
      setSuccess(`Imported ${imported} run${imported !== 1 ? "s" : ""} successfully!`);
      setRuns([]);
    } catch (err: any) {
      setError(err.message || "Failed to import runs");
    } finally {
      setImporting(false);
    }
  }

  function formatDate(dateStr: string) {
    const d = new Date(dateStr + "T12:00:00Z");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Header */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 14,
          backgroundColor: "#ffffff",
          borderBottomWidth: 1,
          borderBottomColor: "#e5e7eb",
        }}
      >
        <TouchableOpacity onPress={() => router.replace(`/(tabs)/dogs/${dogId}` as any)} style={{ marginRight: 12 }}>
          <Text style={{ color: "#1B2A4A", fontSize: 16 }}>← Back</Text>
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontWeight: "700", color: "#1B2A4A" }}>Import from Screenshot</Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {success ? (
          <View style={{ backgroundColor: "#f0fdf4", padding: 16, borderRadius: 12, marginBottom: 16, alignItems: "center" }}>
            <Ionicons name="checkmark-circle" size={32} color="#16a34a" />
            <Text style={{ color: "#16a34a", fontWeight: "700", fontSize: 16, marginTop: 8 }}>{success}</Text>
            <TouchableOpacity
              onPress={() => router.back()}
              style={{ marginTop: 12, backgroundColor: "#1B2A4A", paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}
            >
              <Text style={{ color: "#fff", fontWeight: "600" }}>Back to Profile</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Instructions */}
        {!success && (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, padding: 20, borderWidth: 1, borderColor: "#e5e7eb", marginBottom: 20 }}>
            <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A", marginBottom: 10 }}>
              Import from Another App
            </Text>
            <Text style={{ fontSize: 14, color: "#6b7280", lineHeight: 22 }}>
              Take screenshots of your run history from another app, then upload them here. You can select multiple screenshots at once. We'll automatically read the dates and times and import them.
            </Text>
          </View>
        )}

        {/* Upload button */}
        {!success && (
          <TouchableOpacity
            onPress={pickImages}
            disabled={parsing}
            style={{
              backgroundColor: "#1B2A4A",
              borderRadius: 12,
              padding: 16,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
              gap: 8,
              opacity: parsing ? 0.6 : 1,
            }}
          >
            <Ionicons name="images-outline" size={22} color="#ffffff" />
            <Text style={{ color: "#ffffff", fontSize: 16, fontWeight: "700" }}>
              {runs.length > 0 ? "Upload More Screenshots" : "Upload Screenshots"}
            </Text>
          </TouchableOpacity>
        )}

        {/* Parsing indicator */}
        {parsing && (
          <View style={{ alignItems: "center", paddingVertical: 32 }}>
            <ActivityIndicator size="large" color="#F5A623" />
            <Text style={{ color: "#6b7280", marginTop: 12, fontSize: 14 }}>
              Reading screenshot {parseProgress.current} of {parseProgress.total}...
            </Text>
          </View>
        )}

        {/* Preview parsed runs */}
        {runs.length > 0 && !success && (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", overflow: "hidden", marginBottom: 20 }}>
            <View style={{ padding: 16, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
              <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A" }}>
                Found {runs.length} Run{runs.length !== 1 ? "s" : ""}
              </Text>
              <Text style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                Tap to deselect any runs you don't want to import.
              </Text>
            </View>

            {/* Table header */}
            <View style={{ flexDirection: "row", backgroundColor: "#f9fafb", paddingVertical: 8, paddingHorizontal: 16 }}>
              <Text style={{ width: 28, fontSize: 11, fontWeight: "600", color: "#6b7280" }}></Text>
              <Text style={{ flex: 1, fontSize: 11, fontWeight: "600", color: "#6b7280" }}>Date</Text>
              <Text style={{ width: 60, fontSize: 11, fontWeight: "600", color: "#6b7280", textAlign: "center" }}>Time</Text>
              <Text style={{ width: 60, fontSize: 11, fontWeight: "600", color: "#6b7280", textAlign: "center" }}>Speed</Text>
              <Text style={{ width: 55, fontSize: 11, fontWeight: "600", color: "#6b7280", textAlign: "right" }}>Points</Text>
            </View>

            {runs.map((run, i) => (
              <TouchableOpacity
                key={i}
                onPress={() => toggleRun(i)}
                style={{
                  flexDirection: "row",
                  paddingVertical: 12,
                  paddingHorizontal: 16,
                  backgroundColor: run.selected ? "#fff" : "#f9fafb",
                  borderTopWidth: 1,
                  borderTopColor: "#f3f4f6",
                  alignItems: "center",
                  opacity: run.selected ? 1 : 0.4,
                }}
              >
                <View style={{ width: 28 }}>
                  <Ionicons
                    name={run.selected ? "checkbox" : "square-outline"}
                    size={20}
                    color={run.selected ? "#1B2A4A" : "#d1d5db"}
                  />
                </View>
                <Text style={{ flex: 1, fontSize: 14, color: "#374151" }}>{formatDate(run.date)}</Text>
                <Text style={{ width: 60, fontSize: 14, color: "#374151", textAlign: "center" }}>{formatTime(run.timeSeconds)}s</Text>
                <Text style={{ width: 60, fontSize: 14, color: "#F5A623", fontWeight: "600", textAlign: "center" }}>{run.speed.toFixed(2)}</Text>
                <Text style={{ width: 55, fontSize: 14, color: "#374151", textAlign: "right" }}>{run.points.toFixed(2)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Import button */}
        {runs.length > 0 && !success && (
          <TouchableOpacity
            onPress={handleImport}
            disabled={importing || runs.filter((r) => r.selected).length === 0}
            style={{
              backgroundColor: "#F5A623",
              borderRadius: 12,
              padding: 16,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              opacity: importing ? 0.6 : 1,
            }}
          >
            {importing ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Ionicons name="download-outline" size={22} color="#ffffff" />
            )}
            <Text style={{ color: "#ffffff", fontSize: 16, fontWeight: "700" }}>
              {importing ? "Importing..." : `Import ${runs.filter((r) => r.selected).length} Run${runs.filter((r) => r.selected).length !== 1 ? "s" : ""}`}
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
